import VideoService from '@/services/VideoService';
import VideoApiService from '@/services/VideoApiService';

/**
 * Vuex Video Module
 * Manages video playlist state and playback
 * Mirrors the structure of the player.js module for audio
 */

const state = {
  videos: [],
  isLoadingVideos: false,
  currentVideoIndex: 0,
  currentVideoDetails: null,
  isPlaying: false,
  hasUnlockedPlayback: false,
  pendingTapToPlay: false,
  isShuffleMode: false,
  repeatMode: 0, // 0: off, 1: all videos, 2: one video
  isUserHoldingProgressSlider: false,
  currentProgress: 0,
  currentTime: 0,
  duration: 0,
};

const getters = {
  videos: (state) => state.videos,
  isLoadingVideos: (state) => state.isLoadingVideos,
  currentVideo: (state) => state.videos[state.currentVideoIndex],
  currentVideoIndex: (state) => state.currentVideoIndex,
  currentVideoDetails: (state) => state.currentVideoDetails,
  isPlaying: (state) => state.isPlaying,
  hasUnlockedPlayback: (state) => state.hasUnlockedPlayback,
  pendingTapToPlay: (state) => state.pendingTapToPlay,
  needsPlaybackUnlock: (state) => {
    if (state.hasUnlockedPlayback) return false;
    if (typeof navigator === 'undefined') return false;
    const isIOS = /(iPad|iPhone|iPod)/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isIOS;
  },
  isShuffleMode: (state) => state.isShuffleMode,
  repeatMode: (state) => state.repeatMode,
  currentProgress: (state) => state.currentProgress,
  currentTime: (state) => state.currentTime,
  duration: (state) => state.duration,

  // Sort videos by year (like audio tracks)
  sortedVideos: (state, getters, rootState) => {
    const sortOrder = rootState.ui?.sortOrder || 'chronologically';
    const videos = [...state.videos];

    if (sortOrder === 'alphabetically') {
      return videos.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Chronological - group by year, then by date
    return videos.sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year; // Newest first
      }
      return new Date(b.date) - new Date(a.date);
    });
  },

  // Group videos by year (like audio tracks)
  videosByYear: (state, getters) => {
    const sorted = getters.sortedVideos;
    const grouped = {};

    sorted.forEach(video => {
      const year = video.year || 'Unknown';
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(video);
    });

    return grouped;
  },

  repeatIcon: (state) => {
    switch (state.repeatMode) {
      case 0:
        return 'mdi-repeat';
      case 1:
        return 'mdi-repeat';
      case 2:
        return 'mdi-repeat-once';
      default:
        return 'mdi-repeat';
    }
  },
};

const mutations = {
  SET_VIDEOS(state, videos) {
    state.videos = videos;
  },

  SET_LOADING_VIDEOS(state, isLoading) {
    state.isLoadingVideos = isLoading;
  },

  SET_CURRENT_VIDEO_INDEX(state, index) {
    state.currentVideoIndex = index;
  },

  SET_CURRENT_VIDEO_DETAILS(state, details) {
    state.currentVideoDetails = details;
  },

  SET_IS_PLAYING(state, isPlaying) {
    state.isPlaying = isPlaying;
  },

  SET_HAS_UNLOCKED_PLAYBACK(state, value) {
    state.hasUnlockedPlayback = value;
  },

  SET_PENDING_TAP_TO_PLAY(state, value) {
    state.pendingTapToPlay = value;
  },

  SET_SHUFFLE_MODE(state, isShuffleMode) {
    state.isShuffleMode = isShuffleMode;
  },

  SET_REPEAT_MODE(state, repeatMode) {
    state.repeatMode = repeatMode;
  },

  SET_USER_HOLDING_PROGRESS_SLIDER(state, isHolding) {
    state.isUserHoldingProgressSlider = isHolding;
  },

  SET_CURRENT_PROGRESS(state, progress) {
    state.currentProgress = progress;
  },

  SET_CURRENT_TIME(state, time) {
    state.currentTime = time;
  },

  SET_DURATION(state, duration) {
    state.duration = duration;
  },

  UPDATE_VIDEO_VIEWS(state, { id, views }) {
    const video = state.videos.find(v => v.id === id);
    if (video) {
      video.numberOfViews = views;
    }
  },

  REMOVE_VIDEO(state, id) {
    state.videos = state.videos.filter(v => v.id !== id);
  },
};

const actions = {
  /**
   * Soft-delete a video (super admin only)
   */
  async deleteVideo({ commit, rootGetters, dispatch }, videoID) {
    const userID = rootGetters['auth/userID'];
    if (!userID || !videoID) return;

    try {
      await VideoApiService.deleteVideo(videoID, userID);
      commit('REMOVE_VIDEO', videoID);
      dispatch('ui/showSnackbar', {
        text: 'Moved to trash — recoverable for 30 days',
        timeout: 5000,
      }, { root: true });
    } catch (error) {
      console.error('Failed to delete video:', error);
      dispatch('ui/showSnackbar', 'Failed to delete video', { root: true });
      throw error;
    }
  },

  /**
   * Load videos from the backend API
   */
  async loadVideos({ commit }) {
    commit('SET_LOADING_VIDEOS', true);
    try {
      const videos = await VideoApiService.getVideos();
      commit('SET_VIDEOS', videos);
      return videos;
    } catch (error) {
      console.error('Failed to load videos:', error);
      throw error;
    } finally {
      commit('SET_LOADING_VIDEOS', false);
    }
  },

  /**
   * Initialize video player with first video in list
   */
  async initializePlayer({ state, dispatch }) {
    // Load videos from API if not already loaded
    if (state.videos.length === 0) {
      await dispatch('loadVideos');
    }

    if (state.videos.length > 0) {
      // Load the first video by default
      dispatch('loadVideo', { index: 0, autoplay: false });
    }
  },

  /**
   * Load a video by index
   * @param {number} index - Video index
   * @param {boolean} autoplay - Whether to autoplay (default: false)
   */
  loadVideo({ state, commit }, { index, autoplay = false }) {
    if (index < 0 || index >= state.videos.length) {
      console.error('Invalid video index');
      return;
    }

    const video = state.videos[index];
    commit('SET_CURRENT_VIDEO_INDEX', index);
    commit('SET_CURRENT_VIDEO_DETAILS', video);

    VideoService.loadVideo(video.youtubeId, autoplay);

    // If autoplay is true, set playing state
    if (autoplay) {
      commit('SET_IS_PLAYING', true);
    }
  },

  /**
   * Play the current video
   */
  playVideo({ commit, rootGetters, dispatch }) {
    // Fade out and pause audio if it's playing to avoid both playing at once
    if (rootGetters['player/isPlaying']) {
      dispatch('player/pauseTrackWithFade', 150, { root: true });
    }

    VideoService.play();
    commit('SET_IS_PLAYING', true);

    // Close search bar when video starts playing (for mobile fullscreen)
    if (rootGetters['ui/isShowingSearchBar']) {
      dispatch('ui/toggleSearchBar', null, { root: true });
    }
  },

  /**
   * Pause the current video
   */
  pauseVideo({ commit }) {
    VideoService.pause();
    commit('SET_IS_PLAYING', false);
  },

  /**
   * Pause the current video with fade out
   */
  async pauseVideoWithFade({ commit }, fadeDuration = 200) {
    await VideoService.fadeOutAndPause(fadeDuration);
    commit('SET_IS_PLAYING', false);
  },

  /**
   * Toggle play/pause
   */
  togglePlayPause({ state, dispatch }) {
    if (state.isPlaying) {
      dispatch('pauseVideo');
    } else {
      dispatch('playVideo');
    }
  },

  /**
   * Go to next video
   */
  nextVideo({ state, dispatch }) {
    let nextIndex;

    if (state.isShuffleMode) {
      // Random video
      nextIndex = Math.floor(Math.random() * state.videos.length);
    } else {
      nextIndex = state.currentVideoIndex + 1;

      // Handle repeat modes
      if (nextIndex >= state.videos.length) {
        if (state.repeatMode === 1) {
          // Repeat all - go to first
          nextIndex = 0;
        } else {
          // Don't play anything
          return;
        }
      }
    }

    dispatch('changeVideo', { index: nextIndex, autoplay: false });
  },

  /**
   * Go to previous video
   */
  previousVideo({ state, dispatch }) {
    let prevIndex;

    if (state.currentTime > 3) {
      // If more than 3 seconds into video, restart current video
      VideoService.seek(0);
      return;
    }

    if (state.isShuffleMode) {
      // Random video
      prevIndex = Math.floor(Math.random() * state.videos.length);
    } else {
      prevIndex = state.currentVideoIndex - 1;

      if (prevIndex < 0) {
        if (state.repeatMode === 1) {
          // Go to last video
          prevIndex = state.videos.length - 1;
        } else {
          prevIndex = 0;
        }
      }
    }

    dispatch('changeVideo', { index: prevIndex, autoplay: false });
  },

  /**
   * Change to a specific video
   * @param {number} index - Video index
   * @param {boolean} autoplay - Whether to autoplay (default: false)
   */
  changeVideo({ dispatch, rootGetters }, { index, autoplay = false }) {
    // Close search bar when video starts playing (for mobile fullscreen)
    if (autoplay && rootGetters['ui/isShowingSearchBar']) {
      dispatch('ui/toggleSearchBar', null, { root: true });
    }

    if (autoplay) {
      // Load with autoplay enabled
      dispatch('loadVideo', { index, autoplay: true });
    } else {
      // Load without autoplay and manually play
      dispatch('loadVideo', { index, autoplay: false });
      dispatch('playVideo');
    }
  },

  /**
   * Mark playback as unlocked (iOS Safari first-play gesture captured)
   */
  unlockPlayback({ state, commit }) {
    commit('SET_HAS_UNLOCKED_PLAYBACK', true);
    commit('SET_PENDING_TAP_TO_PLAY', false);

    // Increment view count for the video that was waiting for tap-to-play
    const video = state.videos[state.currentVideoIndex];
    if (video) {
      VideoApiService.incrementViews(video.id)
        .then(response => {
          if (response.status === 'success') {
            commit('UPDATE_VIDEO_VIEWS', { id: video.id, views: response.updatedNumberOfViews });
          }
        })
        .catch(error => {
          console.error('Failed to increment video views:', error);
        });
    }
  },

  /**
   * Select a video (user clicked in list)
   * IMPORTANT: This must be synchronous to preserve iOS Safari user gesture context.
   * Any async work (like incrementViews) is fire-and-forget after playback starts.
   */
  selectVideo({ state, getters, commit, dispatch, rootGetters }, { videoID, index }) {
    // If videoID is provided, find the correct index in the original videos array
    let actualIndex = index;
    if (videoID !== undefined) {
      actualIndex = state.videos.findIndex(video => video.id === videoID);
      if (actualIndex === -1) {
        console.error('Video not found with ID:', videoID);
        return;
      }
    }

    // Update the current video details immediately so the track list shows correct selection
    const video = state.videos[actualIndex];
    commit('SET_CURRENT_VIDEO_INDEX', actualIndex);
    commit('SET_CURRENT_VIDEO_DETAILS', video);

    // On iOS before first unlock: load the video without autoplay and show the
    // tap-to-play overlay so the user's tap establishes iframe user activation.
    if (getters.needsPlaybackUnlock) {
      // Close search bar if open
      if (rootGetters['ui/isShowingSearchBar']) {
        dispatch('ui/toggleSearchBar', null, { root: true });
      }
      VideoService.loadVideo(video.youtubeId, false);
      commit('SET_PENDING_TAP_TO_PLAY', true);
      return;
    }

    // Check if search bar is open (mobile video mode hides player during search)
    const searchBarWasOpen = rootGetters['ui/isShowingSearchBar'];

    // Close search bar first if it's open
    if (searchBarWasOpen) {
      dispatch('ui/toggleSearchBar', null, { root: true });

      // Wait for DOM to update (player to become visible) before loading/playing
      setTimeout(() => {
        VideoService.loadVideo(video.youtubeId, true);
        commit('SET_IS_PLAYING', true);
      }, 100);
    } else {
      // Search bar not open, load and play immediately
      dispatch('changeVideo', { index: actualIndex, autoplay: true });
    }

    // Increment view count AFTER initiating playback (fire-and-forget)
    VideoApiService.incrementViews(video.id)
      .then(response => {
        if (response.status === 'success') {
          commit('UPDATE_VIDEO_VIEWS', { id: video.id, views: response.updatedNumberOfViews });
        }
      })
      .catch(error => {
        console.error('Failed to increment video views:', error);
      });
  },

  /**
   * Seek to a position (percentage)
   */
  seekToPosition({ commit }, positionPercent) {
    VideoService.seek(positionPercent);
    commit('SET_CURRENT_PROGRESS', positionPercent);
  },

  /**
   * Toggle shuffle mode
   */
  toggleShuffle({ state, commit }) {
    commit('SET_SHUFFLE_MODE', !state.isShuffleMode);
  },

  /**
   * Toggle repeat mode (0 -> 1 -> 2 -> 0)
   */
  toggleRepeat({ state, commit }) {
    const nextMode = (state.repeatMode + 1) % 3;
    commit('SET_REPEAT_MODE', nextMode);
  },

  /**
   * Handle video ended
   */
  handleVideoEnded({ state, dispatch }) {
    if (state.repeatMode === 2) {
      // Repeat one - replay current video
      VideoService.seek(0);
      dispatch('playVideo');
    } else {
      // Go to next video
      dispatch('nextVideo');
    }
  },

  /**
   * Update progress from VideoService
   */
  updateProgress({ commit, state }, progressData) {
    if (!state.isUserHoldingProgressSlider) {
      commit('SET_CURRENT_PROGRESS', progressData.percent);
      commit('SET_CURRENT_TIME', progressData.currentTime);
      commit('SET_DURATION', progressData.duration);
    }
  },

  /**
   * User started dragging progress slider
   */
  startHoldingProgressSlider({ commit }) {
    commit('SET_USER_HOLDING_PROGRESS_SLIDER', true);
  },

  /**
   * User stopped dragging progress slider
   */
  stopHoldingProgressSlider({ commit }) {
    commit('SET_USER_HOLDING_PROGRESS_SLIDER', false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
